import {
  absoluteAssetUrl,
  absoluteUrl,
  getSeoForRoute,
  getSiteUrl,
} from "../config/seo.js";
import { content, getServicePage } from "../config/content.js";

const JSON_LD_ATTR = "data-seo-jsonld";

const SOCIAL_PLACEHOLDERS = new Set([
  "https://linkedin.com",
  "https://www.linkedin.com",
  "https://x.com",
  "https://twitter.com",
  "https://github.com",
]);

/**
 * @param {string} attr
 * @param {string} key
 * @param {string} contentValue
 */
function upsertMeta(attr, key, contentValue) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", contentValue);
}

/**
 * @param {string} rel
 * @param {string} href
 */
function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function clearJsonLd() {
  document.head.querySelectorAll(`script[${JSON_LD_ATTR}]`).forEach((n) => n.remove());
}

/**
 * @param {object | object[]} data
 */
function injectJsonLd(data) {
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute(JSON_LD_ATTR, "true");
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

function realSocialUrls() {
  return (content.footer?.social || [])
    .map((s) => s.href)
    .filter((href) => {
      if (!href || !href.startsWith("http")) return false;
      return !SOCIAL_PLACEHOLDERS.has(href.replace(/\/$/, ""));
    });
}

/**
 * @param {import("../config/seo.js").SeoPage} page
 */
function buildOrganization(page) {
  const sameAs = realSocialUrls();
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${getSiteUrl()}/#organization`,
    name: content.brand || "Taldel",
    url: absoluteUrl("/"),
    logo: absoluteAssetUrl("Taldel Black.png"),
    email: content.contact?.email || "hello@taldel.com",
    description: page.description,
    ...(sameAs.length ? { sameAs } : {}),
  };
}

/**
 * @param {import("../config/seo.js").SeoPage} page
 * @param {string} pageUrl
 */
function buildService(page, pageUrl) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: page.title.replace(/^Taldel —\s*/, ""),
    description: page.description,
    url: pageUrl,
    provider: {
      "@type": "Organization",
      "@id": `${getSiteUrl()}/#organization`,
      name: "Taldel",
    },
  };
}

/**
 * @param {string} slug
 * @param {string} pageUrl
 */
function buildFaqPage(slug, pageUrl) {
  const service = getServicePage(slug);
  const items = service?.faqs?.items;
  if (!items?.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    url: pageUrl,
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

/**
 * Apply document head SEO for the current route.
 * @param {{ page?: "home" | "service", slug?: string | null }} [route]
 * @param {import("../config/seo.js").SeoPage} [seoPage]
 */
export function applySeo(route, seoPage) {
  const page = seoPage || getSeoForRoute(route);
  const siteUrl = getSiteUrl();
  const pageUrl = absoluteUrl(page.canonicalPath, siteUrl);
  const imageUrl = absoluteAssetUrl(page.ogImage, siteUrl);

  document.title = page.title;

  upsertMeta("name", "description", page.description);
  upsertLink("canonical", pageUrl);

  upsertMeta("property", "og:type", "website");
  upsertMeta("property", "og:site_name", "Taldel");
  upsertMeta("property", "og:title", page.title);
  upsertMeta("property", "og:description", page.description);
  upsertMeta("property", "og:url", pageUrl);
  upsertMeta("property", "og:image", imageUrl);

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", page.title);
  upsertMeta("name", "twitter:description", page.description);
  upsertMeta("name", "twitter:image", imageUrl);

  clearJsonLd();

  if (page.jsonLd.includes("organization")) {
    injectJsonLd(buildOrganization(page));
  }
  if (page.jsonLd.includes("service") && page.serviceSlug) {
    injectJsonLd(buildService(page, pageUrl));
    const faq = buildFaqPage(page.serviceSlug, pageUrl);
    if (faq) injectJsonLd(faq);
  }
}
